import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Grid, Chip, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Alert, IconButton, Menu, List, ListItem, ListItemText,
  ListItemSecondaryAction, Checkbox, Avatar, ListItemAvatar,
  Stepper, Step, StepLabel, ListSubheader, Skeleton, Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckIcon,
  CheckCircle,
  Cancel,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Comment as CommentIcon,
  CheckBox as CheckBoxIcon,
  AccountTree as WorkflowIcon,
  TrendingUp as TrendingUpIcon,
  DoneAll as DoneAllIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  MeetingRoom as MeetingIcon
} from '@mui/icons-material';
import api from '../api';

const COMMON_DOCUMENT_TYPES = ['W-2', '1099-INT', '1099-DIV', '1099-NEC', '1099-K', '1099-R', '1099-G', '1099-B', '1099-MISC', '1099-C', '1099-Q', '1099-SA', 'Schedule K-1', 'Prior Year Return', 'Bank Statements', 'Mortgage Statement', 'Charitable Donations', 'Medical Expenses', 'Other'];

const EnhancedWorkflowManager = ({ clientId, onWorkflowUpdate, embedded }) => {
  const { userPermissions } = useContext(AuthContext) || {};
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [workflowToRemove, setWorkflowToRemove] = useState(null);
  const [workflowStats, setWorkflowStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    overdue: 0
  });
  const [assignStep, setAssignStep] = useState(0);
  const [taskCustomizations, setTaskCustomizations] = useState({});
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyTeams, setCompanyTeams] = useState([]);
  const [workflowAssignedTo, setWorkflowAssignedTo] = useState(''); // 'self', 'user_123', or 'team_456'
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [workflowMenuAnchor, setWorkflowMenuAnchor] = useState(null);
  const [workflowMenuTarget, setWorkflowMenuTarget] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingTaskId, setRejectingTaskId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch workflows for the client (returns data for use in handlers)
  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/multi-tenant/clients/${clientId}/workflows/`);
      const data = response.data?.results ?? response.data;
      const workflowsList = Array.isArray(data) ? data : [];
      setWorkflows(workflowsList);
      const stats = {
        total: workflowsList.length,
        active: workflowsList.filter(w => w.status === 'ACTIVE').length,
        completed: workflowsList.filter(w => w.status === 'COMPLETED').length,
        overdue: workflowsList.filter(w => w.status === 'OVERDUE').length
      };
      setWorkflowStats(stats);
      return workflowsList;
    } catch {
      setError('Failed to fetch workflows');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch available templates
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/multi-tenant/workflow-templates/');
      const data = response.data?.results ?? response.data;
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setTemplates([]);
    }
  };

  // Fetch company users for assignee dropdown
  const fetchCompanyUsers = async () => {
    try {
      const response = await api.get('/api/multi-tenant/users/');
      const data = response.data?.results ?? response.data;
      setCompanyUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Fetch company teams for assignee dropdown
  const fetchCompanyTeams = async () => {
    try {
      const response = await api.get('/api/multi-tenant/teams/');
      const data = response.data?.results ?? response.data;
      setCompanyTeams(Array.isArray(data) ? data : []);
    } catch {
      setCompanyTeams([]);
    }
  };

  // Initialize task customizations when template is selected (document_types only; assign is workflow-level)
  const initTaskCustomizations = (template) => {
    const customizations = {};
    const isDocumentTask = (t) =>
      t.task_type === 'document' ||
      t.name?.toLowerCase().includes('document') ||
      t.name?.toLowerCase().includes('gather');
    (template?.tasks || []).forEach((task) => {
      customizations[task.id] = {
        document_types: isDocumentTask(task) ? [] : null
      };
    });
    setTaskCustomizations(customizations);
  };

  // Assign workflow to client
  const handleAssignWorkflow = async () => {
    try {
      const task_customizations = (selectedTemplate?.tasks || []).map((task) => ({
        task_template_id: task.id,
        document_types: taskCustomizations[task.id]?.document_types || null
      }));
      const payload = {
        template_id: selectedTemplate.id,
        client_id: clientId,
        task_customizations
      };
      if (workflowAssignedTo === 'self') {
        payload.assigned_to_id = 'self';
      } else if (workflowAssignedTo?.startsWith('user_')) {
        payload.assigned_to_id = parseInt(workflowAssignedTo.replace('user_', ''), 10);
      } else if (workflowAssignedTo?.startsWith('team_')) {
        payload.assigned_to_team_id = parseInt(workflowAssignedTo.replace('team_', ''), 10);
      }
      await api.post('/api/multi-tenant/assign-workflow/', payload);
      
      setAssignDialogOpen(false);
      setSelectedTemplate(null);
      setAssignStep(0);
      setTaskCustomizations({});
      setWorkflowAssignedTo('');
      await fetchWorkflows();
      if (onWorkflowUpdate) onWorkflowUpdate();
    } catch (error) {
      setError('Failed to assign workflow: ' + (error.response?.data?.error || error.message));
    }
  };

  // Remove (cancel) workflow
  const handleRemoveWorkflow = async (workflowId) => {
    try {
      await api.post(`/api/multi-tenant/cancel-workflow/${workflowId}/`);
      setRemoveConfirmOpen(false);
      setWorkflowToRemove(null);
      await fetchWorkflows();
      if (onWorkflowUpdate) onWorkflowUpdate();
    } catch (err) {
      setError('Failed to remove workflow');
    }
  };

  // Edit workflow - open dialog
  const handleEditClick = (workflow) => {
    setEditingWorkflow(workflow);
    setEditNotes(workflow.notes || '');
    setEditDialogOpen(true);
  };

  // Save workflow edit
  const handleSaveEdit = async () => {
    if (!editingWorkflow) return;
    try {
      await api.patch(`/api/multi-tenant/workflows/${editingWorkflow.id}/`, { notes: editNotes });
      setEditDialogOpen(false);
      setEditingWorkflow(null);
      setEditNotes('');
      await fetchWorkflows();
      if (onWorkflowUpdate) onWorkflowUpdate();
    } catch (err) {
      setError('Failed to update workflow');
    }
  };

  // Complete a task
  const handleCompleteTask = async (taskInstanceId) => {
    try {
      await api.post(`/api/multi-tenant/complete-task/${taskInstanceId}/`, { completion_notes: '' });
      await fetchWorkflows();
      if (onWorkflowUpdate) onWorkflowUpdate();
      if (selectedTask?.id === taskInstanceId) setSelectedTask(null);
    } catch (error) {
      setError('Failed to complete task');
    }
  };

  const handleUpdateTaskStatus = async (taskInstanceId, newStatus, completionNotes = '') => {
    try {
      await api.patch(`/api/multi-tenant/tasks/${taskInstanceId}/update/`, {
        status: newStatus,
        completion_notes: completionNotes
      });
      const list = await fetchWorkflows();
      if (onWorkflowUpdate) onWorkflowUpdate();
      const w = list.find(ww => ww.task_instances?.some(t => t.id === taskInstanceId));
      if (w) {
        const t = w.task_instances.find(tt => tt.id === taskInstanceId);
        if (t) setSelectedTask({ ...t, status: newStatus });
      }
    } catch (err) {
      setError('Failed to update task');
    }
  };

  const handleAddComment = async () => {
    if (!selectedTask || !newComment.trim()) return;
    try {
      await api.post(`/api/multi-tenant/tasks/${selectedTask.id}/comments/`, { content: newComment.trim() });
      setNewComment('');
      const list = await fetchWorkflows();
      if (selectedWorkflow) {
        const w = list.find(ww => ww.id === selectedWorkflow.id);
        const t = w?.task_instances?.find(tt => tt.id === selectedTask.id);
        if (t) setSelectedTask(t);
      }
    } catch (err) {
      setError('Failed to add comment');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!selectedTask || !newChecklistItem.trim()) return;
    try {
      await api.post(`/api/multi-tenant/tasks/${selectedTask.id}/checklist/`, { text: newChecklistItem.trim() });
      setNewChecklistItem('');
      const list = await fetchWorkflows();
      if (selectedWorkflow) {
        const w = list.find(ww => ww.id === selectedWorkflow.id);
        const t = w?.task_instances?.find(tt => tt.id === selectedTask.id);
        if (t) setSelectedTask(t);
      }
    } catch (err) {
      setError('Failed to add checklist item');
    }
  };

  const handleToggleChecklistItem = async (itemId) => {
    if (!selectedTask) return;
    const item = selectedTask.checklist_items?.find(i => i.id === itemId);
    if (!item) return;
    try {
      await api.patch(`/api/multi-tenant/tasks/${selectedTask.id}/checklist/${itemId}/`, {
        is_completed: !item.is_completed
      });
      const list = await fetchWorkflows();
      if (selectedWorkflow) {
        const w = list.find(ww => ww.id === selectedWorkflow.id);
        const t = w?.task_instances?.find(tt => tt.id === selectedTask.id);
        if (t) setSelectedTask(t);
      }
    } catch (err) {
      setError('Failed to update checklist');
    }
  };

  const openTaskDetail = (task, workflow) => {
    setSelectedTask(task);
    setSelectedWorkflow(workflow);
    setTaskDetailOpen(true);
    setNewComment('');
    setNewChecklistItem('');
  };

  const handleApproveTask = async (taskId) => {
    setApprovingId(taskId);
    try {
      await api.post(`/api/multi-tenant/tasks/${taskId}/approve/`);
      fetchWorkflows(); // refresh
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve task.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectTask = (taskId) => {
    setRejectingTaskId(taskId);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const confirmRejectTask = async () => {
    if (!rejectingTaskId) return;
    try {
      await api.post(`/api/multi-tenant/tasks/${rejectingTaskId}/reject/`, { reason: rejectReason });
      setRejectDialogOpen(false);
      fetchWorkflows();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject task.');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'PENDING': 'warning',
      'IN_PROGRESS': 'info',
      'NEEDS_REVIEW': 'secondary',
      'ACTIVE': 'info',
      'COMPLETED': 'success',
      'OVERDUE': 'error',
      'PAUSED': 'default',
      'CANCELLED': 'default',
      'BLOCKED': 'default',
      'APPROVED': 'success',
      'REJECTED': 'error'
    };
    return colors[status] || 'default';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'LOW': 'success',
      'MEDIUM': 'warning',
      'HIGH': 'error',
      'URGENT': 'error'
    };
    return colors[priority] || 'default';
  };

  // Calculate progress percentage
  const calculateProgress = (workflow) => {
    if (!workflow.task_instances || workflow.task_instances.length === 0) return 0;
    const completed = workflow.task_instances.filter(task => task.status === 'COMPLETED').length;
    return Math.round((completed / workflow.task_instances.length) * 100);
  };

  // Get communication icon
  const getCommunicationIcon = (type) => {
    const icons = {
      'Email': <EmailIcon />,
      'Phone': <PhoneIcon />,
      'Meeting': <MeetingIcon />,
      'Document': <AssignmentIcon />
    };
    return icons[type] || <AssignmentIcon />;
  };

  useEffect(() => {
    if (clientId) {
      fetchWorkflows();
      fetchTemplates();
    }
  }, [clientId]);

  useEffect(() => {
    if (assignDialogOpen) {
      fetchCompanyUsers();
      fetchCompanyTeams();
    }
  }, [assignDialogOpen]);

  const statCardSx = {
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
    height: '100%',
    minHeight: 112,
    transition: 'all 0.2s ease',
    '&:hover': { boxShadow: 1, borderColor: 'action.hover' }
  };

  if (loading) {
    return (
      <Box>
        {!embedded && (
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={280} height={40} sx={{ mb: 2 }} />
          <Skeleton variant="text" width={400} height={24} />
        </Box>
        )}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <Skeleton variant="rounded" width={160} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      {!embedded && (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <WorkflowIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
            Workflow Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ ml: 5 }}>
          Track and manage client workflows. Assign templates, monitor progress, and complete tasks.
        </Typography>
      </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4, width: '100%', minWidth: 0 }}>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card sx={{ ...statCardSx, flex: 1, minWidth: 0 }}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 }, width: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'text.primary' }}>
                {workflowStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card sx={{ ...statCardSx, borderLeft: '3px solid', borderLeftColor: 'info.main', flex: 1, minWidth: 0 }}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 }, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 18, color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Active
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'info.main' }}>
                {workflowStats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card sx={{ ...statCardSx, borderLeft: '3px solid', borderLeftColor: 'success.main', flex: 1, minWidth: 0 }}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 }, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DoneAllIcon sx={{ fontSize: 18, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Completed
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'success.main' }}>
                {workflowStats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: 'flex', minWidth: 0 }}>
          <Card sx={{ ...statCardSx, borderLeft: '3px solid', borderLeftColor: 'error.main', flex: 1, width: '100%', minWidth: 0 }}>
            <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ fontSize: 18, color: 'error.main' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Overdue
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: 'error.main' }}>
                {workflowStats.overdue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAssignDialogOpen(true)}
            sx={{
              px: 2.5,
              py: 1.25,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            Assign Workflow
          </Button>
          <Tooltip title="Refresh">
            <IconButton color="secondary" onClick={fetchWorkflows} aria-label="Refresh workflows" size="medium">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Workflow List or Empty State */}
      {workflows.length === 0 ? (
        <Card sx={{ borderRadius: 2, border: '1px dashed', borderColor: 'divider', py: 8, textAlign: 'center' }}>
          <CardContent>
            <WorkflowIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              No workflows assigned
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Assign a workflow template to track document collection, tasks, and deadlines for this client.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAssignDialogOpen(true)}
              sx={{ fontWeight: 600, borderRadius: 2, textTransform: 'none' }}
            >
              Assign Workflow
            </Button>
          </CardContent>
        </Card>
      ) : (
      <Grid container spacing={3} sx={{ width: '100%', minWidth: 0 }}>
        {workflows.map((workflow) => (
          <Grid item xs={12} md={6} key={workflow.id} sx={{ minWidth: 0, maxWidth: '100%' }}>
            <Card
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                maxWidth: '100%',
                overflow: 'hidden',
                '&:hover': { boxShadow: 2, borderColor: 'action.selected' }
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3, flex: '1 1 160px', minWidth: 0, wordBreak: 'break-word' }}>
                    {workflow.template?.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Chip
                      label={workflow.status}
                      color={getStatusColor(workflow.status)}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setWorkflowMenuAnchor(e.currentTarget); setWorkflowMenuTarget(workflow); }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                {workflow.template?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {workflow.template.description}
                  </Typography>
                )}

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Progress
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: calculateProgress(workflow) === 100 ? 'success.main' : 'text.primary' }}>
                      {calculateProgress(workflow)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={calculateProgress(workflow)}
                    sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover' }}
                  />
                </Box>

                {/* Task List */}
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                  Tasks
                </Typography>
                <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                <List dense disablePadding sx={{ minWidth: { xs: 280, sm: 0 } }}>
                  {workflow.task_instances?.slice(0, 5).map((task) => (
                    <ListItem
                      key={task.id}
                      sx={{
                        px: 1.5,
                        py: 1,
                        mx: -1.5,
                        borderRadius: 1,
                        cursor: 'pointer',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: 'action.hover' },
                        '& .MuiListItemSecondaryAction-root': {
                          position: { xs: 'static', sm: 'absolute' },
                          transform: { xs: 'none', sm: 'translateY(-50%)' },
                          marginLeft: { xs: 'auto', sm: 0 },
                          marginTop: { xs: 1, sm: 0 },
                          width: { xs: '100%', sm: 'auto' },
                          display: 'flex',
                          justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                          flexWrap: 'wrap',
                          gap: 0.5,
                        },
                      }}
                      onClick={() => openTaskDetail(task, workflow)}
                    >
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: task.status === 'COMPLETED' ? 'success.main' : 'action.selected',
                            color: task.status === 'COMPLETED' ? 'white' : 'text.secondary'
                          }}
                        >
                          {task.status === 'COMPLETED' ? <CheckIcon sx={{ fontSize: 16 }} /> : <ScheduleIcon sx={{ fontSize: 16 }} />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ minWidth: 0, pr: { xs: 0, sm: 18 } }}
                        primary={task.task_template?.name}
                        primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                        secondary={
                          <Box component="span" display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip label={task.status} size="small" color={getStatusColor(task.status)} sx={{ height: 20 }} />
                            {(task.comments?.length > 0 || task.checklist_items?.length > 0) && (
                              <Typography variant="caption" color="textSecondary">
                                {task.comments?.length > 0 && `${task.comments.length} comments`}
                                {task.comments?.length > 0 && task.checklist_items?.length > 0 && ' · '}
                                {task.checklist_items?.length > 0 && `${task.checklist_items.filter(c => c.is_completed).length}/${task.checklist_items.length} checklist`}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction onClick={(e) => e.stopPropagation()} sx={{ right: 8 }}>
                        {!['COMPLETED', 'CANCELLED'].includes(task.status) && (
                          <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                            {task.status === 'PENDING' && (
                              <Button size="small" onClick={() => handleUpdateTaskStatus(task.id, 'IN_PROGRESS')}>
                                Start
                              </Button>
                            )}
                            {['PENDING', 'IN_PROGRESS'].includes(task.status) && (
                              <Button size="small" color="secondary" onClick={() => handleUpdateTaskStatus(task.id, 'NEEDS_REVIEW')}>
                                Needs Review
                              </Button>
                            )}
                            {['PENDING', 'IN_PROGRESS', 'NEEDS_REVIEW'].includes(task.status) && (
                              <Button size="small" color="success" onClick={() => handleCompleteTask(task.id)}>
                                Complete
                              </Button>
                            )}
                            {task.status === 'NEEDS_REVIEW' && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircle />}
                                  onClick={() => handleApproveTask(task.id)}
                                  disabled={approvingId === task.id}
                                >
                                  {approvingId === task.id ? 'Approving...' : 'Approve'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<Cancel />}
                                  onClick={() => handleRejectTask(task.id)}
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                          </Box>
                        )}
                        {task.status === 'COMPLETED' && (
                          <Chip label="Done" color="success" size="small" />
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {workflow.task_instances?.length > 5 && (
                    <ListItem sx={{ px: 1.5, py: 1, mx: -1.5 }}>
                      <ListItemText
                        primary={`+${workflow.task_instances.length - 5} more tasks`}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      )}

      {/* Workflow Card Context Menu */}
      <Menu
        anchorEl={workflowMenuAnchor}
        open={Boolean(workflowMenuAnchor)}
        onClose={() => { setWorkflowMenuAnchor(null); setWorkflowMenuTarget(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 160, borderRadius: 2, mt: 1.5 } }}
      >
        <MenuItem
          onClick={() => {
            if (workflowMenuTarget) handleEditClick(workflowMenuTarget);
            setWorkflowMenuAnchor(null);
            setWorkflowMenuTarget(null);
          }}
          sx={{ py: 1.5 }}
        >
          <EditIcon sx={{ mr: 1.5, fontSize: 20 }} /> Edit notes
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (workflowMenuTarget) {
              setWorkflowToRemove(workflowMenuTarget);
              setRemoveConfirmOpen(true);
            }
            setWorkflowMenuAnchor(null);
            setWorkflowMenuTarget(null);
          }}
          disabled={workflowMenuTarget?.status === 'COMPLETED' || workflowMenuTarget?.status === 'CANCELLED'}
          sx={{ py: 1.5, color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} /> Remove workflow
        </MenuItem>
      </Menu>

      {/* Assign Workflow Dialog - Multi-step */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => {
          setAssignDialogOpen(false);
          setAssignStep(0);
          setSelectedTemplate(null);
          setTaskCustomizations({});
          setWorkflowAssignedTo('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pb: 1 }}>
          Assign Workflow to Client
        </DialogTitle>
        <DialogContent sx={{ overflowY: 'auto', pt: 0 }}>
          <Stepper activeStep={assignStep} sx={{ mb: 3 }}>
            <Step><StepLabel>Select Template</StepLabel></Step>
            <Step><StepLabel>Customize Tasks</StepLabel></Step>
          </Stepper>

          {assignStep === 0 && (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select a workflow template to assign to this client:
              </Typography>
              <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto' }}>
              <Grid container spacing={2}>
                {templates.map((template) => (
                  <Grid item xs={12} sm={6} key={template.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: selectedTemplate?.id === template.id ? 'primary.main' : 'divider',
                        bgcolor: selectedTemplate?.id === template.id ? 'action.selected' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' }
                      }}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{template.name}</Typography>
                        <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
                          {template.description || 'No description'}
                        </Typography>
                        <Chip
                          label={`${template.tasks?.length || 0} tasks`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 500 }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              </Box>
            </>
          )}

          {assignStep === 1 && selectedTemplate && (
            <Box>
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Assign to</InputLabel>
                <Select
                  value={workflowAssignedTo}
                  label="Assign to"
                  onChange={(e) => setWorkflowAssignedTo(e.target.value || '')}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 320, zIndex: 1400 } },
                    MenuListProps: { sx: { py: 0 } },
                    disableScrollLock: true
                  }}
                  renderValue={(v) => {
                    if (!v) return 'Unassigned';
                    if (v === 'self') return 'Assign to myself';
                    if (v.startsWith('user_')) {
                      const uid = parseInt(v.replace('user_', ''), 10);
                      const u = companyUsers.find(x => x.id === uid);
                      return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.username : v;
                    }
                    if (v.startsWith('team_')) {
                      const tid = parseInt(v.replace('team_', ''), 10);
                      const t = companyTeams.find(x => x.id === tid);
                      return t?.name || v;
                    }
                    return v;
                  }}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  <MenuItem value="self">Assign to myself</MenuItem>
                  <ListSubheader sx={{ bgcolor: 'action.hover', lineHeight: 2, fontWeight: 600 }}>Users</ListSubheader>
                  {companyUsers.map((u) => (
                    <MenuItem key={`u-${u.id}`} value={`user_${u.id}`} sx={{ pl: 3 }}>
                      {u.first_name} {u.last_name} ({u.email || u.username})
                    </MenuItem>
                  ))}
                  <ListSubheader sx={{ bgcolor: 'action.hover', lineHeight: 2, fontWeight: 600 }}>Teams</ListSubheader>
                  {companyTeams.map((t) => (
                    <MenuItem key={`t-${t.id}`} value={`team_${t.id}`} sx={{ pl: 3 }}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
                Customize tasks (select document types for document tasks)
              </Typography>
              <List sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                {(selectedTemplate.tasks || []).map((task, idx) => (
                  <ListItem key={task.id} sx={{ flexDirection: 'column', alignItems: 'stretch', border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2, p: 2, bgcolor: 'action.hover' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2">
                        {idx + 1}. {task.name}
                      </Typography>
                      <Chip label={task.task_type || 'manual'} size="small" variant="outlined" />
                    </Box>
                    {task.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {task.description}
                      </Typography>
                    )}
                    {(task.task_type === 'document' || task.name?.toLowerCase().includes('document') || task.name?.toLowerCase().includes('gather')) && (
                      <Box mt={2}>
                        <Typography variant="caption" display="block" gutterBottom>
                          Documents to request:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {COMMON_DOCUMENT_TYPES.map((docType) => {
                            const selected = (taskCustomizations[task.id]?.document_types || []).includes(docType);
                            return (
                              <Chip
                                key={docType}
                                label={docType}
                                size="small"
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() => {
                                  setTaskCustomizations((prev) => {
                                    const current = prev[task.id]?.document_types || [];
                                    const next = selected
                                      ? current.filter((d) => d !== docType)
                                      : [...current, docType];
                                    return {
                                      ...prev,
                                      [task.id]: { ...prev[task.id], document_types: next }
                                    };
                                  });
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              if (assignStep === 1) {
                setAssignStep(0);
                setWorkflowAssignedTo('');
              } else {
                setAssignDialogOpen(false);
                setAssignStep(0);
                setSelectedTemplate(null);
                setWorkflowAssignedTo('');
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {assignStep === 1 ? 'Back' : 'Cancel'}
          </Button>
          {assignStep === 0 ? (
            <Button
              variant="contained"
              disabled={!selectedTemplate}
              onClick={() => {
                initTaskCustomizations(selectedTemplate);
                fetchCompanyUsers();
                fetchCompanyTeams();
                setWorkflowAssignedTo('');
                setAssignStep(1);
              }}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Next: Customize Tasks
            </Button>
          ) : (
            <Button variant="contained" onClick={handleAssignWorkflow} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Assign Workflow
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Workflow Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Workflow Notes</DialogTitle>
        <DialogContent>
          {editingWorkflow && (
            <TextField
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 1 }}
              placeholder="Add notes about this workflow..."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Remove Workflow Confirmation */}
      <Dialog open={removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Remove Workflow</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Are you sure you want to remove this workflow? This will cancel the workflow and all pending tasks.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveConfirmOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => workflowToRemove && handleRemoveWorkflow(workflowToRemove.id)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Task Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Task</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            multiline rows={3} fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRejectTask}>
            Reject Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Detail Dialog - Comments, Checklist, Status */}
      <Dialog
        open={taskDetailOpen}
        onClose={() => { setTaskDetailOpen(false); setSelectedTask(null); setSelectedWorkflow(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}
      >
        {selectedTask && (
          <>
            <DialogTitle sx={{ flexShrink: 0, fontWeight: 600, pb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedTask.task_template?.name}</Typography>
                <Chip label={selectedTask.status} color={getStatusColor(selectedTask.status)} size="small" sx={{ fontWeight: 600 }} />
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
              {selectedTask.task_template?.description && (
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  {selectedTask.task_template.description}
                </Typography>
              )}
              {selectedTask.assigned_to_name && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Assigned to: {selectedTask.assigned_to_name}
                </Typography>
              )}

              {/* Status */}
              <Typography variant="caption" gutterBottom sx={{ mt: 2, display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</Typography>
              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <Select
                  value={selectedTask.status}
                  onChange={(e) => handleUpdateTaskStatus(selectedTask.id, e.target.value)}
                  disabled={['COMPLETED', 'CANCELLED'].includes(selectedTask.status)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="NEEDS_REVIEW">Needs Review</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>

              {/* Checklist */}
              <Typography variant="caption" gutterBottom sx={{ mt: 2, display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <CheckBoxIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 16 }} />
                Checklist
              </Typography>
              <List dense>
                {(selectedTask.checklist_items || []).map((item) => (
                  <ListItem key={item.id} sx={{ py: 0 }}>
                    <Checkbox
                      checked={item.is_completed}
                      onChange={() => handleToggleChecklistItem(item.id)}
                      size="small"
                    />
                    <ListItemText
                      primary={item.text}
                      sx={{ textDecoration: item.is_completed ? 'line-through' : 'none' }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box display="flex" gap={1} mt={1}>
                <TextField
                  size="small"
                  placeholder="Add checklist item..."
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                  fullWidth
                />
                <Button variant="outlined" size="small" onClick={handleAddChecklistItem}>Add</Button>
              </Box>

              {/* Comments */}
              <Typography variant="caption" gutterBottom sx={{ mt: 3, display: 'block', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <CommentIcon sx={{ verticalAlign: 'middle', mr: 0.5, fontSize: 16 }} />
                Comments
              </Typography>
              <List dense>
                {(selectedTask.comments || []).map((c) => (
                  <ListItem key={c.id} alignItems="flex-start" sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <Typography variant="body2">{c.content}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {c.user_name} · {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <Box display="flex" gap={1} mt={1}>
                <TextField
                  size="small"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                  fullWidth
                  multiline
                  maxRows={3}
                />
                <Button variant="outlined" size="small" onClick={handleAddComment}>Post</Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              {!['COMPLETED', 'CANCELLED'].includes(selectedTask.status) && (
                <Button onClick={() => handleCompleteTask(selectedTask.id)} color="success" variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                  Complete Task
                </Button>
              )}
              <Button onClick={() => { setTaskDetailOpen(false); setSelectedTask(null); setSelectedWorkflow(null); }} sx={{ textTransform: 'none' }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default EnhancedWorkflowManager;


